import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';

/**
 * Componente Toast para exibir mensagens de feedback
 * Desktop: canto superior direito (estilo macOS)
 * Mobile: topo centralizado (estilo iOS)
 * 
 * @param {Object} props - Propriedades do componente
 * @param {string} props.message - Mensagem a ser exibida
 * @param {string} props.type - Tipo da mensagem ('success', 'error', 'warning', 'info')
 * @param {number} props.duration - Duração em ms para exibição
 * @param {Function} props.onClose - Callback ao fechar
 * @returns {JSX.Element} Componente React
 */
const Toast = ({ 
  message, 
  type = 'success', 
  duration = 3000, 
  onClose 
}) => {
  const [isVisible, setIsVisible] = useState(true);
  const [isMobile, setIsMobile] = useState(false);
  
  // Detectar se é mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);
  
  // Controlar visibilidade e remover componente
  useEffect(() => {
    if (!message) return;
    
    const timer = setTimeout(() => {
      setIsVisible(false);
      
      setTimeout(() => {
        onClose && onClose();
      }, 300);
    }, duration);
    
    return () => clearTimeout(timer);
  }, [message, duration, onClose]);
  
  // Definir estilos baseados no tipo
  const getTypeStyles = () => {
    switch(type) {
      case 'success':
        return 'bg-green-50 border-l-4 border-green-500 text-green-800';
      case 'error':
        return 'bg-red-50 border-l-4 border-red-500 text-red-800';
      case 'warning':
        return 'bg-yellow-50 border-l-4 border-yellow-500 text-yellow-800';
      case 'info':
      default:
        return 'bg-blue-50 border-l-4 border-blue-500 text-blue-800';
    }
  };
  
  // Definir ícone baseado no tipo
  const getIconByType = () => {
    switch(type) {
      case 'success':
        return (
          <svg className="w-5 h-5 text-green-500" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
          </svg>
        );
      case 'error':
        return (
          <svg className="w-5 h-5 text-red-500" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
          </svg>
        );
      case 'warning':
        return (
          <svg className="w-5 h-5 text-yellow-500" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
          </svg>
        );
      case 'info':
      default:
        return (
          <svg className="w-5 h-5 text-blue-500" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
          </svg>
        );
    }
  };
  
  // Estilos condicional baseado no dispositivo
  const positionClasses = isMobile
    ? "fixed top-0 left-1/2 transform -translate-x-1/2 z-[99999] w-[90%] max-w-sm mt-safe" // Estilo iOS centralizado com safe area
    : "fixed top-4 right-4 z-[99999] max-w-sm"; // Estilo macOS no canto superior direito
  
  // Componente não renderiza se não houver mensagem
  if (!message) return null;
  
  return createPortal(
    <div 
      className={`${positionClasses} transform transition-all duration-300 ${
        isVisible ? 'translate-y-0 opacity-100' : 'translate-y-[-20px] opacity-0'
      }`}
      style={{
        // Aplicar marginTop para respeitar a área segura em iPhones com notch
        marginTop: isMobile ? 'env(safe-area-inset-top, 10px)' : '0'
      }}
    >
      <div className={`rounded-lg shadow-lg p-4 flex items-center ${getTypeStyles()}`}>
        <div className="flex-shrink-0 mr-3">
          {getIconByType()}
        </div>
        <div className="flex-1 text-sm font-medium">
          {message}
        </div>
        <button 
          onClick={() => setIsVisible(false)} 
          className="ml-3 flex-shrink-0 text-gray-400 hover:text-gray-600 focus:outline-none"
          aria-label="Fechar"
        >
          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
            <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
          </svg>
        </button>
      </div>
    </div>,
    document.body
  );
};

export default Toast;
